import React, { useEffect, useState } from "react";
import {
  Container,
  Grid,
  Typography,
  Button,
  Box,
  Card,
  useMediaQuery,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import api, { API_BASE_URL } from "../utils/api";
import ProfileCard from "../components/OwnerDashboard/ProfileCard";
import WorkOrders from "../components/OwnerDashboard/WorkOrders";
import DashboardCard from "../components/OwnerDashboard/DashboardCard";

const theme = createTheme({
  palette: {
    primary: { main: "#0E2F5A" },
    secondary: { main: "#6c757d" },
    success: { main: "#28a745" },
    danger: { main: "#dc3545" },
  },
  typography: {
    fontFamily: "'Roboto', sans-serif",
    h6: { fontWeight: 600 },
  },
});

const OwnerDashboard = () => {
  const [user, setUser] = useState(null);
  const [memberData, setMemberData] = useState(null);
  const [violationTypes, setViolationTypes] = useState([]);
  const [violations, setViolations] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(false);


  const username = localStorage.getItem("username");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Fetch user + violation types
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!username) return;
      try {
        const userRes = await api.get(`/users/${username}`);
        setUser(userRes.data);

        const typesRes = await api.get(`/getCcrViolationTypes`);
        setViolationTypes(typesRes.data);

        // if user has only one account, auto-select it
        if (userRes.data.AssociatedAccounts?.length === 1) {
          setSelectedAccount(userRes.data.AssociatedAccounts[0]);
        }
      } catch (error) {
        console.error("Error fetching user/violation types:", error);
      }
    };
    fetchInitialData();
  }, [username]);

  // Fetch member info, violations, work orders
useEffect(() => {
  const fetchMemberRelatedData = async () => {
    if (!selectedAccount || violationTypes.length === 0) return;
    try {
      setLoadingWorkOrders(true);   // ✅ start loading
      const memberRes = await api.get(`/members/${selectedAccount}`);
      setMemberData(memberRes.data);

      const violationsRes = await api.get(`/getViolations`, {
        params: { account_id: selectedAccount },
      });

      const enrichedViolations = violationsRes.data.map((v) => {
        const type = violationTypes.find((t) => t.id === v.ccr_violation_type_id);
        return {
          ...v,
          type: type?.title || "N/A",
          description: type?.resolution_step || "No description",
        };
      });
      setViolations(enrichedViolations);

      const workOrdersRes = await api.get(`/getWorkOrders`, {
        params: { property_id: selectedAccount },
      });
      setWorkOrders(workOrdersRes.data);
    } catch (error) {
      console.error("Error fetching member-related data:", error);
    } finally {
      setLoadingWorkOrders(false);  // ✅ stop loading
    }
  };
  fetchMemberRelatedData();
}, [selectedAccount, violationTypes]);


  // Fetch documents
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const res = await api.get(`/owner-documents`);
        setDocuments(res.data);
      } catch (error) {
        console.error("Error loading documents:", error);
      }
    };
    fetchDocuments();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: "primary.main",
              display: "flex",
              alignItems: "center",
              mb: 1,
            }}
          >
            <HomeWorkIcon sx={{ mr: 1 }} />
            My Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Welcome back, {user?.Name || "Homeowner"}
          </Typography>
        </Box>

        {/* If multiple accounts, let user choose */}
        {user?.AssociatedAccounts?.length > 1 && !selectedAccount && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Select an account to view:
              </Typography>
            </Grid>
            {user.AssociatedAccounts.map((acc) => (
              <Grid item xs={12} md={4} key={acc}>
                <Card
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    textAlign: "center",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "grey.100" },
                  }}
                  onClick={() => setSelectedAccount(acc)}
                >
                  <Typography variant="h6" color="primary.main">
                    {acc}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Only render the dashboard if an account is selected */}
        {selectedAccount && (
          <>
            {/* Go Back Button */}
            {user?.AssociatedAccounts?.length > 1 && (
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setSelectedAccount(null)}
                >
                  ← Go Back to Account Selection
                </Button>
              </Box>
            )}

            {/* Profile + Documents */}
            <Grid container spacing={3} alignItems="stretch" sx={{ mb: 5 }}>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%", p: 2, borderRadius: 3 }}>
                  <ProfileCard user={{ ...user, ...memberData }} />
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 2, height: "100%", borderRadius: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    HOA Documents
                  </Typography>
                  {documents.length === 0 ? (
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        textAlign: "center",
                        color: "grey.500",
                        minHeight: 240,
                      }}
                    >
                      <InsertDriveFileIcon sx={{ fontSize: 80, mb: 2, color: "grey.400" }} />
                      <Typography variant="body1" color="text.secondary">
                        No documents uploaded yet.
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ maxHeight: 260, overflowY: "auto", pr: 1 }}>
                      {documents.slice(0, 5).map((doc) => (
                        <Box key={doc.id} sx={{ mb: 2 }}>
                          <Typography fontWeight={600} variant="body2">
                            <InsertDriveFileIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                            <a
                              href={`${API_BASE_URL}/api/owner-documents/download/${doc.stored_filename}`}
                              download
                              style={{
                                textDecoration: "none",
                                color: theme.palette.primary.main,
                              }}
                            >
                              {doc.description || doc.original_filename}
                            </a>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            🕒 {new Date(doc.upload_date).toLocaleDateString()}
                          </Typography>
                        </Box>
                      ))}
                      {documents.length > 5 && (
                        <Button
                          size="small"
                          variant="text"
                          href="/owner-documents"
                          sx={{ mt: 1, textTransform: "none" }}
                        >
                          View all documents
                        </Button>
                      )}
                    </Box>
                  )}
                </Card>
              </Grid>
            </Grid>

            {/* Work Orders */}
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <DashboardCard title="Work Orders">
                  <WorkOrders
                    workOrders={workOrders}
                    propertyId={selectedAccount}
                    openerUserId={user?.UserID}
                    loading={loadingWorkOrders}
                  />
                </DashboardCard>
              </Grid>
            </Grid>
          </>
        )}
      </Container>
    </ThemeProvider>
  );
};

export default OwnerDashboard;
